/**
 * Mobile Home Screen — replaces the desktop Dashboard on small screens.
 *
 * Shows a focused, action-oriented view for sales reps:
 * - Greeting + date
 * - 3 KPI cards (calls today, connects, queue depth)
 * - "Call Next" big button
 * - Overdue callbacks
 * - Overdue tasks
 * - Recent calls
 */

import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Phone, PhoneCall, AlertTriangle, CheckSquare, ArrowRight } from 'lucide-react'
import { queueApi, voipApi } from '../../api/endpoints'
import { useAuthStore } from '../../stores/auth'
import { StatusBadge, SegmentBadge } from '../../components/ui/Badge'
import { fmtRelative } from '../../utils/date'

export default function MobileHomePage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)

  // Queue stats
  const { data: statsData } = useQuery({
    queryKey: ['queue-stats'],
    queryFn: () => queueApi.stats(),
    refetchInterval: 30000,
  })
  const stats = statsData?.data

  // Top queue items (for Call Next + callbacks)
  const { data: queueData } = useQuery({
    queryKey: ['queue', 'up_next', 0],
    queryFn: () => queueApi.list({ tab: 'up_next', limit: '1' }),
  })
  const nextLead = queueData?.data?.[0]

  // Overdue callbacks
  const { data: callbackData } = useQuery({
    queryKey: ['queue', 'callbacks', 0],
    queryFn: () => queueApi.list({ tab: 'callbacks', limit: '5' }),
  })
  const callbacks = callbackData?.data || []

  // Recent calls
  const { data: recentData } = useQuery({
    queryKey: ['voip-calls-recent-home'],
    queryFn: () => voipApi.list({ limit: '5', offset: '0' }),
    refetchInterval: 30000,
  })
  const recentCalls = recentData?.calls || []

  // Greeting based on time
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const dateStr = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })

  return (
    <div className="space-y-4 -mt-2">
      {/* Greeting */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">{greeting}, {user?.full_name?.split(' ')[0] || user?.username}</h1>
        <p className="text-sm text-gray-500">{dateStr}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-2">
        <KpiCard label="Calls Today" value={stats?.calls_today ?? 0} color="blue" />
        <KpiCard label="Interacted" value={stats?.outcomes_today?.interacted ?? 0} color="purple" />
        <KpiCard label="Queue" value={stats?.up_next ?? 0} color="indigo" />
      </div>

      {/* Call Outcome Breakdown */}
      {stats?.outcomes_today && Object.keys(stats.outcomes_today).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-2">Today's Outcomes</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { key: 'interacted', label: 'Interacted', color: 'text-purple-600' },
              { key: 'connected', label: 'Connected', color: 'text-green-600' },
              { key: 'answered', label: 'Answered', color: 'text-blue-600' },
              { key: 'no_answer', label: 'No Answer', color: 'text-gray-500' },
            ].map(({ key, label, color }) => (
              <div key={key} className="text-center">
                <p className={`text-lg font-bold ${color}`}>{stats.outcomes_today?.[key] || 0}</p>
                <p className="text-[10px] text-gray-400">{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Call Next Button */}
      {nextLead && (
        <button
          onClick={() => navigate(`/voip?lead=${nextLead.id}`)}
          className="w-full py-4 px-5 bg-green-600 active:bg-green-700 text-white font-semibold rounded-xl shadow-lg flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <Phone size={20} />
            <div className="text-left">
              <p className="text-base">Call Next</p>
              <p className="text-sm text-green-100">{nextLead.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {nextLead.quality_segment && (
              <SegmentBadge segment={nextLead.quality_segment} />
            )}
            <ArrowRight size={18} />
          </div>
        </button>
      )}

      {/* SLA Warning */}
      {stats && (stats.sla_breached > 0 || stats.sla_warning > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-3">
          <AlertTriangle size={18} className="text-red-500 flex-shrink-0" />
          <div className="text-sm">
            {stats.sla_breached > 0 && <span className="font-semibold text-red-700">{stats.sla_breached} SLA breached</span>}
            {stats.sla_breached > 0 && stats.sla_warning > 0 && <span className="text-red-400"> · </span>}
            {stats.sla_warning > 0 && <span className="text-amber-700">{stats.sla_warning} SLA warning</span>}
          </div>
        </div>
      )}

      {/* Overdue Callbacks */}
      {callbacks.length > 0 && (
        <Section title="Upcoming Callbacks" count={callbacks.length} onViewAll={() => navigate('/queue?tab=callbacks')}>
          {callbacks.slice(0, 3).map((cb) => (
            <div key={cb.id} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{cb.name}</p>
                <p className="text-xs text-gray-500">
                  {cb.next_call_date ? fmtRelative(cb.next_call_date) : 'Scheduled'}
                  {cb.quality_segment && <span className="ml-1.5">{cb.quality_segment}</span>}
                </p>
              </div>
              <button
                onClick={() => navigate(`/voip?lead=${cb.id}`)}
                className="ml-3 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg active:bg-green-700 flex items-center gap-1"
              >
                <Phone size={12} /> Call
              </button>
            </div>
          ))}
        </Section>
      )}

      {/* Recent Calls */}
      {recentCalls.length > 0 && (
        <Section title="Recent Calls" onViewAll={() => navigate('/voip')}>
          {recentCalls.slice(0, 5).map((call: any) => (
            <div key={call.id} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                call.direction === 'inbound' ? 'bg-blue-50' : 'bg-green-50'
              }`}>
                {call.direction === 'inbound' ? (
                  <PhoneCall size={14} className="text-blue-600" />
                ) : (
                  <Phone size={14} className="text-green-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {call.lead_name || call.from_number || 'Unknown'}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <StatusBadge status={call.status} />
                  {call.duration_seconds != null && call.duration_seconds > 0 && (
                    <span>{Math.floor(call.duration_seconds / 60)}m {call.duration_seconds % 60}s</span>
                  )}
                  <span>{fmtRelative(call.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Quick Links */}
      <div className="grid grid-cols-2 gap-2 pt-2">
        <QuickLink icon={PhoneCall} label="Lead Queue" onClick={() => navigate('/queue')} />
        <QuickLink icon={CheckSquare} label="My Tasks" onClick={() => navigate('/tasks')} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    purple: 'bg-purple-50 text-purple-700',
  }
  return (
    <div className={`rounded-xl p-3 ${colors[color] || colors.blue}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-[11px] font-medium uppercase tracking-wider opacity-70">{label}</p>
    </div>
  )
}

function Section({ title, count, children, onViewAll }: {
  title: string
  count?: number
  children: React.ReactNode
  onViewAll?: () => void
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">
          {title}
          {count != null && count > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">{count}</span>
          )}
        </h3>
        {onViewAll && (
          <button onClick={onViewAll} className="text-xs text-indigo-600 font-medium active:text-indigo-800">
            View all
          </button>
        )}
      </div>
      <div className="px-4">{children}</div>
    </div>
  )
}

function QuickLink({ icon: Icon, label, onClick }: {
  icon: React.ComponentType<{ size?: number; className?: string }>
  label: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2.5 bg-white border border-gray-200 rounded-xl p-3.5 active:bg-gray-50 text-left"
    >
      <Icon size={18} className="text-indigo-500" />
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </button>
  )
}
